import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import GenericSelect from '../../components/ui/GenericSelect';
import PmAnalyticsFilterBar from '../../components/performance/PmAnalyticsFilterBar';
import usePmAnalyticsFilters, { latestPeriod } from '../../components/performance/pmAnalyticsFilters';
import { useAuth } from '../../context/AuthContext';
import { Grid, PageEmpty, PageWrap } from '../performance-operations/styles';
import { fetchPeriods, usePanel } from '../performance-operations/config';
import NineBoxKpis from './components/NineBoxKpis';
import NineBoxMatrixPanel from './components/NineBoxMatrixPanel';
import TransitionPanel from './components/TransitionPanel';
import NineBoxPeopleTable from './components/NineBoxPeopleTable';
import { AttentionPanel, MissingAxisPanel } from './components/CoveragePanels';
import { Hint, SelectorField, SelectorRow } from './styles';
import {
  Q_PREVIOUS, Q_PROCESS, Q_SELECTION, fetchProcesses, fetchSummary, fetchTransitions, parseSelection, serializeSelection,
} from './config';

// PM analitik P5 — E09 "9-Box ve Yetenek Görünümü" (/talent/nine-box-transitions; analiz §12). İK ve yetenek kurulu için
// performans ve potansiyeli ayrı eksenlerde: M29–M32 kartları, 3×3 matris (sayı + yüzde; eksik eksenliler ayrı), dönemler arası
// geçiş matrisi, dikkat bulguları ve seçili hücre/geçiş listesi. Ortak süzgeç şeridi (dönem + kapsam) URL'de; süreç seçimi ve
// liste seçimi de URL'de (geri dönüşte korunur). Panel başına bağımsız yükleme/hata/Tekrar Dene. TD modülü kapalıysa rota/menü
// kilitli, uçlar 409 döner. Potansiyel erişimi olmayan yönetici yalnız performans bandını görür.
export default function TalentNineBoxTransitionsPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { isHr: isHrSession, canRead, canWrite } = useAuth();
  const { filters, setFilters, reset } = usePmAnalyticsFilters();
  const [periods, setPeriods] = useState([]);
  const [periodsLoaded, setPeriodsLoaded] = useState(false);
  const [processes, setProcesses] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchPeriods().then((list) => { if (alive) { setPeriods(list); setPeriodsLoaded(true); } });
    fetchProcesses().then((list) => { if (alive) setProcesses(list); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!periodsLoaded || periods.length === 0) return;
    if (!filters.periodId || !periods.some((p) => p.id === filters.periodId)) {
      setFilters((f) => ({ ...f, periodId: latestPeriod(periods).id }));
    }
  }, [periodsLoaded, periods, filters.periodId, setFilters]);

  // Süreç ve liste seçimi URL'de (ortak süzgeç anahtarları korunur).
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const processSel = useMemo(() => ({ processId: query.get(Q_PROCESS) || null, previousProcessId: query.get(Q_PREVIOUS) || null }),
    [query]);
  const selection = useMemo(() => parseSelection(query.get(Q_SELECTION)), [query]);
  const setParam = useCallback((key, value) => {
    const p = new URLSearchParams(location.search);
    if (value) p.set(key, value); else p.delete(key);
    const s = p.toString();
    history.replace({ pathname: location.pathname, search: s ? `?${s}` : '' });
  }, [history, location.pathname, location.search]);
  const onSelect = useCallback((sel) => setParam(Q_SELECTION, serializeSelection(sel)), [setParam]);

  // URL'deki dönem listede yoksa (bayat yer imi) paneller o dönemle istek atmaz; dönem etkisi en yeni döneme geçirir.
  const ready = periodsLoaded && periods.some((p) => p.id === filters.periodId);
  const key = JSON.stringify({ filters, processSel });
  const summary = usePanel(() => fetchSummary(filters, processSel), [key], ready);
  const transitions = usePanel(() => fetchTransitions(filters, processSel), [key], ready);
  const potentialVisible = summary.data ? summary.data.potentialVisible : true;

  const processOptions = useMemo(() => processes.map((p) => ({ id: p.id, name: p.name })), [processes]);
  const previousOptions = useMemo(() => [
    { id: 'none', name: t('talent.nineBoxTransitions.previousNone') },
    ...processOptions.filter((p) => p.id !== (processSel.processId || summary.data?.process?.id)),
  ], [processOptions, processSel.processId, summary.data, t]);

  // İK rolü ayrımı — İK = sunucunun kararı (AuthContext.isHr), Şirket Yöneticisi DEĞİL.
  const isHr = Boolean(isHrSession);
  return (
    <PageWrap>
      <Breadcrumbs items={[{ label: t('talent.nineBoxTransitions.title') }]} />
      {periodsLoaded && periods.length === 0 ? <PageEmpty data-state="no-periods">{t('performance.operations.noPeriods')}</PageEmpty> : null}
      {periods.length > 0 ? (
        <PmAnalyticsFilterBar
          periods={periods}
          filters={filters}
          onChange={setFilters}
          onReset={reset}
          lastUpdated={summary.data?.calculatedAt || null}
          stale={false}
          groupMembership={filters.groupIds.length ? 'current' : null}
        />
      ) : null}
      {ready ? (
        <>
          <SelectorRow>
            <SelectorField>
              <GenericSelect
                label={t('talent.nineBoxTransitions.process')}
                value={processSel.processId}
                onChange={(v) => setParam(Q_PROCESS, v)}
                options={processOptions}
                placeholder={summary.data?.process?.name || t('talent.nineBoxTransitions.processDefault')}
              />
            </SelectorField>
            <SelectorField>
              <GenericSelect
                label={t('talent.nineBoxTransitions.previousProcess')}
                value={processSel.previousProcessId}
                onChange={(v) => setParam(Q_PREVIOUS, v)}
                options={previousOptions}
                placeholder={summary.data?.previousProcess?.name || t('talent.nineBoxTransitions.previousDefault')}
              />
            </SelectorField>
          </SelectorRow>
          <Grid key={filters.periodId}>
            <NineBoxKpis panel={summary} />
            <NineBoxMatrixPanel panel={summary} selection={selection} onSelect={onSelect} />
            <MissingAxisPanel panel={summary} selection={selection} onSelect={onSelect} />
            <TransitionPanel panel={transitions} selection={selection} onSelect={onSelect} />
            <AttentionPanel
              panel={summary} selection={selection} onSelect={onSelect}
              canSeeSuccession={canRead('TD')}
            />
            <NineBoxPeopleTable
              filters={filters}
              processes={processSel}
              selection={selection}
              potentialVisible={potentialVisible}
              canInvite={isHr && canWrite('TD')}
              onClearSelection={() => onSelect(null)}
            />
          </Grid>
          <Hint>{t('talent.nineBoxTransitions.footnote')}</Hint>
        </>
      ) : null}
    </PageWrap>
  );
}
